import React, { useState, useEffect, useRef, useCallback } from "react";
import { createFormToken, validateFormTime, isRandomString, requiresWordStructure } from '../utils/botDetection';
import {
  FaCheckCircle,
  FaCalendarAlt,
  FaUserTie,
  FaLightbulb,
  FaClock,
  FaGift,
  FaFileAlt,
  FaArrowUp,
} from "react-icons/fa";
import PolicyModal from "./PolicyModal";
import apiService from '../services/api';
import { formatPhoneNumber, isValidPhoneNumber } from '../utils/helpers';
import analyticsService from '../services/analyticsService';
import { getSessionId } from '../hooks/usePageTracking';

const CitiTekConsultation = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    projectDetails: "",
    agreedToTerms: false,
  });
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    policyType: null,
  });
  const [showScrollTop, setShowScrollTop] = useState(false);

  // ── Security refs ─────────────────────────────────────────────────────────
  const formTokenRef = useRef(createFormToken());

  // ── Analytics tracking refs ───────────────────────────────────────────────
  const funnelViewedRef = useRef(false);
  const funnelStartedRef = useRef(false);
  const draftTimerRef = useRef(null);

  // Fire "form_viewed" once when the section becomes visible
  useEffect(() => {
    if (funnelViewedRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !funnelViewedRef.current) {
          funnelViewedRef.current = true;
          analyticsService.trackFunnelEvent({
            sessionId: getSessionId(),
            eventType: 'form_viewed',
            formType: 'consultation',
          });
        }
      },
      { threshold: 0.3 }
    );
    const el = document.getElementById('consultation');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Debounced draft save — fires 3s after the user stops typing
  const saveDraftDebounced = useCallback((data) => {
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      const fields = Object.entries(data)
        .filter(([k, v]) => k !== 'agreedToTerms' && v)
        .map(([k]) => k);
      const pct = Math.round((fields.length / 5) * 100);
      analyticsService.saveDraft({
        sessionId: getSessionId(),
        formType: 'consultation',
        contactName: data.name || '',
        contactEmail: data.email || '',
        service: data.service || '',
        fieldsCompleted: fields,
        completionPct: pct,
        status: 'in_progress',
      });
    }, 3000);
  }, []);

  useEffect(() => () => clearTimeout(draftTimerRef.current), []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show/hide scroll to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Fire "form_started" once on first interaction
    if (!funnelStartedRef.current) {
      funnelStartedRef.current = true;
      analyticsService.trackFunnelEvent({
        sessionId: getSessionId(),
        eventType: 'form_started',
        formType: 'consultation',
      });
    }

    let updated;
    if (name === "phone") {
      const formattedPhone = formatPhoneNumber(value);
      updated = { ...formData, phone: formattedPhone };
    } else {
      updated = { ...formData, [name]: type === "checkbox" ? checked : value };
    }
    setFormData(updated);
    saveDraftDebounced(updated);
  };

  const openPolicy = (policyType) => {
    setModalState({ isOpen: true, policyType });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, policyType: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot: silently succeed so bots get no signal they were caught
    if (honeypot) {
      setStatus({
        type: "success",
        message: "Thank you! Your consultation request has been submitted successfully. We'll contact you within 24 hours.",
      });
      setFormData({ name: "", email: "", phone: "", service: "", projectDetails: "", agreedToTerms: false });
      return;
    }

    // Timing: bot submissions arrive in milliseconds; real users take seconds
    if (!validateFormTime(formTokenRef.current, 4000)) {
      setStatus({
        type: "error",
        message: "Please take a moment to review your information before submitting.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Content: reject random-string or single-word-blob project details
    if (isRandomString(formData.projectDetails) || !requiresWordStructure(formData.projectDetails)) {
      setStatus({
        type: "error",
        message: "Please provide a meaningful project description with complete sentences.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Validate phone number
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      setStatus({
        type: "error",
        message: "Please enter a valid 10-digit US phone number.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Validate terms agreement
    if (!formData.agreedToTerms) {
      setStatus({
        type: "error",
        message:
          "Please agree to the Terms of Service and Privacy Policy to continue.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const data = await apiService.post('/api/consultation', {
        ...formData,
        _ft: formTokenRef.current,
        website: honeypot,
      }, { includeAuth: false });

      // Mark draft as completed and track funnel submission
      const sid = getSessionId();
      analyticsService.trackFunnelEvent({ sessionId: sid, eventType: 'form_submitted', formType: 'consultation', completionPct: 100 });
      analyticsService.saveDraft({ sessionId: sid, formType: 'consultation', status: 'completed', completionPct: 100 });

      setStatus({
        type: "success",
        message: data.message || "Thank you! Your consultation request has been submitted successfully. We'll contact you within 24 hours."
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        projectDetails: "",
        agreedToTerms: false,
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Failed to submit request. Please try again later.",
      });

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <FaUserTie className="text-2xl" />,
      title: "Strategic Assessment",
      description: "Comprehensive analysis of your technology landscape and business objectives",
    },
    {
      icon: <FaLightbulb className="text-2xl" />,
      title: "Tailored Solutions",
      description: "Custom architecture and implementation strategies aligned with your goals",
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: "Scope & Investment",
      description: "Transparent project scoping with detailed cost and timeline estimates",
    },
    {
      icon: <FaCheckCircle className="text-2xl" />,
      title: "Industry Expertise",
      description: "Battle-tested methodologies and technical best practices",
    },
    {
      icon: <FaGift className="text-2xl" />,
      title: "No Commitment",
      description: "Complimentary consultation with zero obligation to proceed",
    },
    {
      icon: <FaCalendarAlt className="text-2xl" />,
      title: "Rapid Response",
      description: "Priority scheduling and prompt follow-up from our team",
    },
  ];

  return (
    <>
      <section
        id="consultation"
        className="relative py-20 md:py-28 bg-white dark:bg-gray-900 overflow-hidden"
      >
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-accent/5 dark:bg-accent/10 rounded-full filter blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 md:mb-20 animate-fade-in-up">
            <div className="inline-block">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                <span className="text-gray-900 dark:text-white">Free </span>
                <span className="gradient-text-accent">Consultation</span>
              </h2>
              <div className="h-1 bg-gradient-to-r from-accent via-primary to-teal rounded-full"></div>
            </div>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mt-6">
              Connect with our technical experts to discuss your digital transformation goals and explore strategic solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Benefits Section */}
            <div className="space-y-8 animate-slide-in-left">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 dark:bg-accent/20 rounded-full mb-6">
                  <FaGift className="text-accent" />
                  <span className="text-sm font-semibold text-accent">
                    What You'll Get
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Strategic Planning That Delivers Results
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg mb-8">
                  Our complimentary consultation provides an in-depth assessment of your technology
                  requirements, business objectives, and growth opportunities. Receive expert guidance,
                  strategic recommendations, and a clear implementation roadmap—with zero commitment.
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-3 p-4 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex-shrink-0 p-2 bg-gradient-to-br from-accent to-orange-600 dark:from-accent dark:to-orange-500 rounded-lg text-white group-hover:scale-110 transition-transform duration-300">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Response Banner */}
              <div className="bg-gradient-to-r from-accent/10 via-primary/10 to-teal/10 dark:from-accent/20 dark:via-primary/20 dark:to-teal/20 p-6 rounded-2xl backdrop-blur-sm border border-accent/20 dark:border-accent/30">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 p-4 bg-gradient-to-br from-accent to-primary dark:from-accent dark:to-teal rounded-xl text-white">
                    <FaCalendarAlt className="text-3xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                      Priority Response
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Our team will reach out within 24 hours to schedule your
                      personalized consultation session
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultation Form */}
            <div className="animate-slide-in-right">
              <div className="relative bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 md:p-10 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-600">
                {/* Gradient Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-primary/20 to-teal/20 dark:from-accent/30 dark:via-primary/30 dark:to-teal/30 rounded-3xl blur-xl -z-10 opacity-50"></div>

                <div className="mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Request Your Complimentary Consultation
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Share your project details and our team will contact you within 24 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot — visually hidden; bots fill it, humans never see it */}
                  <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Name Field */}
                  <div>
                    <label
                      htmlFor="cons-name"
                      className="block text-gray-900 dark:text-white font-semibold mb-2"
                    >
                      Full Name <span className="text-accent">*</span>
                    </label>
                    <input
                      type="text"
                      id="cons-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="consultation-input w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-400"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="cons-email"
                      className="block text-gray-900 dark:text-white font-semibold mb-2"
                    >
                      Email Address <span className="text-accent">*</span>
                    </label>
                    <input
                      type="email"
                      id="cons-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="consultation-input w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-400"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label
                      htmlFor="cons-phone"
                      className="block text-gray-900 dark:text-white font-semibold mb-2"
                    >
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="cons-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="consultation-input w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-400"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  {/* Service Field */}
                  <div>
                    <label
                      htmlFor="cons-service"
                      className="block text-gray-900 dark:text-white font-semibold mb-2"
                    >
                      Service Interest <span className="text-accent">*</span>
                    </label>
                    <select
                      id="cons-service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="consultation-select w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white cursor-pointer"
                    >
                      <option value="" disabled>
                        Select a service
                      </option>
                      <option value="custom-design">Custom Website Design</option>
                      <option value="responsive-dev">Responsive Web Development</option>
                      <option value="web-software">Custom Web-Based Software Application Development</option>
                      <option value="redesign">Website Redesign & Performance</option>
                      <option value="seo">SEO Setup & Optimization</option>
                      <option value="it-support">IT Support & Troubleshooting</option>
                      <option value="maintenance">Website Maintenance & Updates</option>
                      <option value="hosting">Hosting, Domain & Email Setup</option>
                      <option value="security">Security & Backup Management</option>
                      <option value="consulting">IT Consulting</option>
                      <option value="multiple">Multiple Services</option>
                      <option value="other">Other - I'll Explain in Details</option>
                    </select>
                  </div>

                  {/* Project Details Field */}
                  <div>
                    <label
                      htmlFor="cons-project"
                      className="block text-gray-900 dark:text-white font-semibold mb-2 flex items-center gap-2"
                    >
                      <FaFileAlt className="text-accent" />
                      Project Details <span className="text-accent">*</span>
                    </label>
                    <textarea
                      id="cons-project"
                      name="projectDetails"
                      value={formData.projectDetails}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="consultation-textarea w-full px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                      placeholder="Describe your project objectives, current challenges, technical requirements, timeline considerations, and budget parameters..."
                    ></textarea>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-start gap-2">
                      <span>💡</span>
                      <span>
                        The more context you provide about your business goals and technical
                        requirements, the more valuable our consultation will be.
                      </span>
                    </p>
                  </div>

                  {/* Terms Agreement Checkbox */}
                  <div className="consultation-terms-wrapper bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative flex items-center justify-center mt-0.5">
                        <input
                          type="checkbox"
                          name="agreedToTerms"
                          checked={formData.agreedToTerms}
                          onChange={handleChange}
                          className="consultation-checkbox w-5 h-5 text-accent bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-500 rounded focus:ring-2 focus:ring-accent cursor-pointer transition-all duration-300"
                        />
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                        I agree to the{" "}
                        <button
                          type="button"
                          onClick={() => openPolicy("terms")}
                          className="text-accent hover:text-accent-600 dark:hover:text-orange-400 font-semibold underline underline-offset-2 hover:underline-offset-4 transition-all duration-300"
                        >
                          Terms of Service
                        </button>{" "}
                        and{" "}
                        <button
                          type="button"
                          onClick={() => openPolicy("privacy")}
                          className="text-accent hover:text-accent-600 dark:hover:text-orange-400 font-semibold underline underline-offset-2 hover:underline-offset-4 transition-all duration-300"
                        >
                          Privacy Policy
                        </button>
                        <span className="text-accent ml-1">*</span>
                      </span>
                    </label>
                  </div>

                  {/* Status Message */}
                  {status.message && (
                    <div
                      className={`consultation-status p-4 rounded-xl font-medium animate-fade-in flex items-start gap-3 ${
                        status.type === "success"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-2 border-green-300 dark:border-green-700"
                          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-2 border-red-300 dark:border-red-700"
                      }`}
                    >
                      <FaCheckCircle className={`text-xl flex-shrink-0 mt-0.5 ${status.type === "success" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`} />
                      <p>{status.message}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full px-8 py-4 bg-gradient-to-r from-accent to-orange-600 hover:from-accent-600 hover:to-orange-700 dark:from-accent dark:to-orange-500 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Request Free Consultation
                          <svg
                            className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-4 bg-gradient-to-r from-accent to-orange-600 hover:from-accent-600 hover:to-orange-700 dark:from-accent dark:to-orange-500 text-white rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-110 animate-fade-in"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-xl" />
        </button>
      )}

      {/* Policy Modal */}
      <PolicyModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        policyType={modalState.policyType}
      />
    </>
  );
};

export default CitiTekConsultation;
